/**
 * Draft state and client-side validation for the custom stat field form.
 *
 * Kept out of the component so the rules can be unit-tested and so the form
 * file stays inside the 300-line ceiling. The service remains the authority:
 * these checks exist to give the coach an inline message before a round trip
 * (`stat_key` shape, missing label, enum with no catalog), and every rule here
 * mirrors one in `services/stat-schema/src/http/stat-schema-request.ts`.
 */
import {
  STAT_KEY_MAX_LENGTH,
  STAT_KEY_PATTERN,
  STAT_LABEL_MAX_LENGTH,
  type StatFieldAppliesTo,
  type StatFieldDataType,
  type StatFieldDefinition,
  type StatFieldEnumCategory,
  type StatFieldInput,
  type StatFieldPatch,
} from '../../lib/api-types';

export interface CustomStatDraft {
  statKey: string;
  label: string;
  dataType: StatFieldDataType;
  appliesTo: StatFieldAppliesTo;
  /** Empty unless `dataType === 'enum'`. */
  enumCategory: string;
  hudlExportKey: string;
  isRequired: boolean;
}

export type CustomStatDraftErrors = Partial<Record<keyof CustomStatDraft, string>>;

export function createEmptyCustomStatDraft(): CustomStatDraft {
  return {
    statKey: '',
    label: '',
    dataType: 'numeric',
    appliesTo: 'offense',
    enumCategory: '',
    hudlExportKey: '',
    isRequired: false,
  };
}

export function toCustomStatDraft(field: StatFieldDefinition): CustomStatDraft {
  return {
    statKey: field.stat_key,
    label: field.label,
    dataType: field.data_type,
    appliesTo: field.applies_to,
    enumCategory: field.enum_category ?? '',
    hudlExportKey: field.hudl_export_key ?? '',
    isRequired: field.is_required,
  };
}

/**
 * "Yards After Contact" → `yards_after_contact`. Offered as the machine name
 * while the coach has not typed one, so nobody has to think about snake_case.
 */
export function suggestStatKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, STAT_KEY_MAX_LENGTH);
}

export function validateCustomStatDraft(
  draft: CustomStatDraft,
  options: { isEditing: boolean; availableCategories: readonly string[] }
): CustomStatDraftErrors {
  const errors: CustomStatDraftErrors = {};

  if (draft.label.trim() === '') {
    errors.label = 'Enter a display name — this is the label coaches see.';
  } else if (draft.label.trim().length > STAT_LABEL_MAX_LENGTH) {
    errors.label = `Keep the display name to ${String(STAT_LABEL_MAX_LENGTH)} characters or fewer.`;
  }

  if (!options.isEditing) {
    const key = draft.statKey.trim();
    if (key === '') {
      errors.statKey = 'Enter a field code.';
    } else if (key.length > STAT_KEY_MAX_LENGTH) {
      errors.statKey = `Keep the field code to ${String(STAT_KEY_MAX_LENGTH)} characters or fewer.`;
    } else if (!STAT_KEY_PATTERN.test(key)) {
      errors.statKey =
        'Use lowercase letters, numbers and single underscores only, e.g. yards_after_contact.';
    }
  }

  if (draft.dataType === 'enum') {
    if (draft.enumCategory === '') {
      errors.enumCategory = 'Choose the option list this field picks from.';
    } else if (!options.availableCategories.includes(draft.enumCategory)) {
      errors.enumCategory =
        'That option list has no values yet. Add options in Team Settings first.';
    }
  }

  return errors;
}

export function hasDraftErrors(errors: CustomStatDraftErrors): boolean {
  return Object.keys(errors).length > 0;
}

export function toStatFieldInput(draft: CustomStatDraft): StatFieldInput {
  return {
    stat_key: draft.statKey.trim(),
    label: draft.label.trim(),
    data_type: draft.dataType,
    applies_to: draft.appliesTo,
    enum_category:
      draft.dataType === 'enum' ? (draft.enumCategory as StatFieldEnumCategory) : undefined,
    hudl_export_key: draft.hudlExportKey.trim() === '' ? null : draft.hudlExportKey.trim(),
    is_required: draft.isRequired,
  };
}

/** `stat_key` and `data_type` are never sent: the service rejects both. */
export function toStatFieldPatch(draft: CustomStatDraft): StatFieldPatch {
  return {
    label: draft.label.trim(),
    applies_to: draft.appliesTo,
    enum_category:
      draft.dataType === 'enum' ? (draft.enumCategory as StatFieldEnumCategory) : undefined,
    hudl_export_key: draft.hudlExportKey.trim() === '' ? null : draft.hudlExportKey.trim(),
    is_required: draft.isRequired,
  };
}

export const DATA_TYPE_LABELS: Record<StatFieldDataType, string> = {
  numeric: 'Number',
  enum: 'Pick from a list',
  boolean: 'Yes / no',
  text: 'Short text',
  tag_list: 'Tags',
};

export const APPLIES_TO_LABELS: Record<StatFieldAppliesTo, string> = {
  offense: 'Offense',
  defense: 'Defense',
  special_teams: 'Special Teams',
  general: 'All units',
};

export const DATA_TYPE_HINTS: Record<StatFieldDataType, string> = {
  numeric: 'Whole or decimal numbers. Summed and averaged in the Stat Tracker.',
  enum: 'A dropdown backed by one of your team option lists. Counted by value.',
  boolean: 'A checkbox on the Play Entry form. Counted as a rate.',
  text: 'Free-form note, shown in the archive but not aggregated.',
  tag_list: 'Several short tags per play, shown in the archive and counted by tag.',
};
